import React, { useState, useCallback, useRef } from 'react';
import { CSSObject } from '@emotion/react';
import { useTranslate } from 'nucleus-text';
import useBreakpoints from '@hooks/useBreakpoints';
import BlockHeader from '@cvent/blocks/components/BlockHeader';
import CventSVG from '@illustrations/CventSVG';
import useTheme from '@cvent/blocks/hooks/useTheme';
import ContentSection from '@cvent/blocks/components/templates/ContentSection';
import Button from '@cvent/carina/components/Button';
import Link from 'next/link';
import { LoadingSpinner } from '@cvent/carina/components/LoadingSpinner';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { FOCUS_OUTLINE_IN_FIREFOX } from '@tools/globalStylesConstants';
import { ExternalLinkIcon } from '@components/shared/V1ToV2Icon';
import LayoutSpacer from '@components/layout/Spacer';
import dynamic from 'next/dynamic';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';

const ReactPlayer = dynamic(() => import('react-player'), {
  ssr: false
});

interface Props {
  signupLink: string;
}

const useStyles = (): Record<string, CSSObject> => {
  const { backgroundColor } = useTheme();
  const { mediaIsMobile } = useBreakpoints();

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    titleContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap'
    },
    titleStyle: {
      marginRight: 10,
      [mediaIsMobile]: {
        marginRight: 5
      }
    },
    videoWrapper: {
      position: 'relative',
      width: '100%'
    },
    reactPlayerWrapper: {
      paddingTop: '56.25%',
      '.react-player': {
        position: 'absolute',
        top: 0,
        left: 0
      },
      '.react-player iframe': {
        width: '100% !important',
        height: '100% !important'
      }
    },
    spinnerWrapper: {
      backgroundColor: backgroundColor.base,
      width: '100%'
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: 0,
      backgroundColor: backgroundColor.base,
      borderRadius: 10,
      marginBottom: 0
    }
  };
};

function CTAButton({ signupLink }: Props): JSX.Element {
  const { translate } = useTranslate();
  const { ahSignUpCta } = usePageActionsApi();

  return (
    <>
      <LayoutSpacer size={1 / 2} />
      <Link href={signupLink} passHref legacyBehavior>
        <Button
          text={translate('Cvent-Branding-Button-Text')}
          appearance="lined"
          size="l"
          element="a"
          target="_blank"
          css={{
            width: 'fit-content',
            ...FOCUS_OUTLINE_IN_FIREFOX
          }}
          rel="noopener noreferrer"
          accessibilityLabel={translate('Cvent-Branding-Button-Accessibility-Text')}
          icon={ExternalLinkIcon}
          onClick={() => {
            ahSignUpCta();
          }}
        />
      </Link>
    </>
  );
}

function DescriptionBlock({
  title,
  description,
  signupLink
}: {
  title: string;
  description: string;
  signupLink: string;
}): JSX.Element {
  const { isMobile } = useBreakpoints();
  const { container, titleContainer, titleStyle } = useStyles();
  const { font, block } = useTheme();

  return (
    <div css={container}>
      <BlockHeader>
        <span css={titleContainer}>
          <span css={titleStyle}>{title}</span>
          <CventSVG color={block.isWhite || block.isLight ? null : font.color.base} />
        </span>
      </BlockHeader>
      <p>{description}</p>
      {!isMobile && <CTAButton signupLink={signupLink} />}
    </div>
  );
}

function CventBrandingBlock({ signupLink }: Props): JSX.Element {
  const { videoWrapper, spinnerWrapper, spinner, reactPlayerWrapper } = useStyles();
  const { translate } = useTranslate();
  const { isMobile } = useBreakpoints();
  const [ready, setReady] = useState(false);
  const playerRef = useRef();
  const { ahPlayVideoCta } = usePageActionsApi();

  const onVideoPlayerPlay = useCallback(() => {
    setReady(true);
  }, [setReady]);

  return (
    <ContentSection
      textContent={
        <DescriptionBlock
          title={translate('Cvent-Branding-Title')}
          description={translate('Cvent-Branding-Description')}
          signupLink={signupLink}
        />
      }
      mediaContent={
        <>
          <div css={videoWrapper}>
            <div css={reactPlayerWrapper} {...injectTestId('cvent-branding-video')}>
              <ReactPlayer
                ref={playerRef}
                className="react-player"
                controls
                url="https://www.youtube-nocookie.com/embed/dMr1oqRR8GY"
                width="100%"
                height="100%"
                loop
                playsinline
                onReady={onVideoPlayerPlay}
                muted
                style={{
                  overflow: 'hidden',
                  borderRadius: 10
                }}
                onPlay={() => {
                  ahPlayVideoCta();
                }}
              />
            </div>
            {!ready && (
              <div css={spinnerWrapper}>
                <LoadingSpinner aria-label={translate('Universal-Loading')} size="m" css={spinner} />
              </div>
            )}
          </div>
          {isMobile && <CTAButton signupLink={signupLink} />}
        </>
      }
    />
  );
}

export default CventBrandingBlock;
